import * as THREE from 'three';
import type { PortalDef, PropInstance } from '../../zones/types';
import type { AssetLibrary } from '../assets';
import type { ParticleSystem } from './Particles';
import type { LightSource, PropLights } from './PropLights';

type PortalStyle = 'ring' | 'gate';

interface PortalItem {
  def: PortalDef;
  group: THREE.Group;
  style: PortalStyle;
  color: THREE.Color;
  materials: THREE.Material[];
  level: { value: number };
  open: boolean;
  label: THREE.Sprite;
  labelTex: THREE.CanvasTexture;
  base: THREE.Vector3;
  facing: number;
  /** Curtain / vortex parts hidden while sealed. */
  active: THREE.Object3D[];
  frame: THREE.Object3D | null;
}

const KIND_COLOR: Record<PortalDef['kind'], THREE.Color> = {
  exit: new THREE.Color(1.0, 0.8, 0.45),
  dock: new THREE.Color(0.45, 0.85, 1.0),
  dungeon: new THREE.Color(0.72, 0.36, 1.0),
  instance: new THREE.Color(0.55, 0.45, 1.0),
};
const SEALED = new THREE.Color(0.38, 0.44, 0.56);
const KIND_LABEL: Record<PortalDef['kind'], string> = { exit: 'Exit', dock: 'Harbour', dungeon: 'Dungeon', instance: 'Onward' };

const VERT = /* glsl */ `
varying vec2 vUv;
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const FOG_FADE = /* glsl */ `
  #ifdef USE_FOG
    #ifdef FOG_EXP2
      float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    gl_FragColor.rgb *= 1.0 - fogFactor;
  #endif`;

const NOISE = /* glsl */ `
float pHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float pNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(pHash(i), pHash(i + vec2(1, 0)), u.x), mix(pHash(i + vec2(0, 1)), pHash(i + vec2(1, 1)), u.x), u.y);
}`;

const RUNE_FRAG = /* glsl */ `
uniform sampler2D map;
uniform vec3 uColor;
uniform float uTime;
uniform float uLevel;
uniform float uSpin;
varying vec2 vUv;
#include <fog_pars_fragment>
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  if (r > 1.0) discard;
  float c = cos(uTime * uSpin), s = sin(uTime * uSpin);
  vec2 q = mat2(c, -s, s, c) * p;
  float glyph = texture2D(map, q * 0.5 + 0.5).a;
  float fill = (1.0 - smoothstep(0.0, 1.0, r)) * 0.28;
  float pulse = 0.82 + 0.18 * sin(uTime * 2.2);
  gl_FragColor = vec4(uColor * (glyph * 2.0 + fill) * pulse * uLevel, 1.0);
  ${FOG_FADE}
}`;

const CURTAIN_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
uniform float uLevel;
varying vec2 vUv;
#include <fog_pars_fragment>
${NOISE}
void main() {
  float y = vUv.y;
  float streak = pNoise(vec2(vUv.x * 26.0, y * 2.4 - uTime * 0.9));
  float streak2 = pNoise(vec2(vUv.x * 61.0 + 7.0, y * 5.0 - uTime * 1.7));
  float a = pow(1.0 - y, 1.9) * (0.2 + 0.5 * streak + 0.35 * streak2) * smoothstep(0.0, 0.06, y);
  gl_FragColor = vec4(uColor * a * 1.05 * uLevel, 1.0);
  ${FOG_FADE}
}`;

const VORTEX_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
uniform float uLevel;
varying vec2 vUv;
#include <fog_pars_fragment>
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  if (r > 1.0) discard;
  float ang = atan(p.y, p.x);
  float sw1 = sin(ang * 3.0 + r * 9.0 - uTime * 2.6) * 0.5 + 0.5;
  float sw2 = sin(ang * 5.0 - r * 15.0 + uTime * 1.9) * 0.5 + 0.5;
  float core = smoothstep(0.55, 0.0, r);
  float rim = smoothstep(0.72, 0.95, r) * smoothstep(1.0, 0.94, r);
  float a = (sw1 * 0.6 + sw2 * 0.35) * (1.0 - r) * 1.2 + core * 0.45 + rim * 1.7;
  vec3 col = mix(uColor, vec3(1.0), core * 0.35);
  gl_FragColor = vec4(col * a * uLevel, 1.0);
  ${FOG_FADE}
}`;

let runeTex: THREE.CanvasTexture | null = null;
/** Procedural rune circle: concentric bands, ticks and glyph dashes (alpha mask). */
function runeTexture(): THREE.CanvasTexture {
  if (runeTex) return runeTex;
  const s = 512;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  g.translate(s / 2, s / 2);
  g.strokeStyle = 'rgba(255,255,255,1)';
  g.fillStyle = 'rgba(255,255,255,1)';
  const ring = (r: number, w: number) => {
    g.lineWidth = w;
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.stroke();
  };
  ring(s * 0.47, 5);
  ring(s * 0.44, 2);
  ring(s * 0.3, 3);
  ring(s * 0.27, 1.5);
  // Ticks between the outer bands.
  for (let i = 0; i < 72; i++) {
    const a = (i / 72) * Math.PI * 2;
    const long = i % 6 === 0;
    g.lineWidth = long ? 3 : 1.5;
    g.beginPath();
    g.moveTo(Math.cos(a) * s * 0.44, Math.sin(a) * s * 0.44);
    g.lineTo(Math.cos(a) * s * (long ? 0.39 : 0.415), Math.sin(a) * s * (long ? 0.39 : 0.415));
    g.stroke();
  }
  // Glyph dashes in the middle band.
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 12; i++) {
    const a0 = (i / 12) * Math.PI * 2 + 0.08;
    g.save();
    g.rotate(a0);
    g.translate(s * 0.345, 0);
    g.lineWidth = 3;
    g.beginPath();
    const k = 3 + Math.floor(rnd() * 3);
    for (let j = 0; j < k; j++) {
      const x0 = (rnd() - 0.5) * 22;
      const y0 = (rnd() - 0.5) * 30;
      g.moveTo(x0, y0);
      g.lineTo(x0 + (rnd() - 0.5) * 20, y0 + (rnd() - 0.5) * 26);
    }
    g.stroke();
    g.restore();
  }
  // Inner star.
  g.lineWidth = 2.5;
  g.beginPath();
  for (let i = 0; i <= 8; i++) {
    const a = (i / 8) * Math.PI * 2 * 3;
    const x = Math.cos(a) * s * 0.25;
    const y = Math.sin(a) * s * 0.25;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.stroke();
  runeTex = new THREE.CanvasTexture(c);
  return runeTex;
}

function drawLabel(canvas: HTMLCanvasElement, title: string, sub: string, sealed: boolean): void {
  const g = canvas.getContext('2d');
  if (!g) return;
  const w = canvas.width;
  const h = canvas.height;
  g.clearRect(0, 0, w, h);
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `600 46px Cinzel, 'Cormorant Garamond', Georgia, serif`;
  g.lineJoin = 'round';
  g.lineWidth = 8;
  g.strokeStyle = 'rgba(8,8,12,0.85)';
  g.strokeText(title, w / 2, h * 0.4);
  const grd = g.createLinearGradient(0, h * 0.2, 0, h * 0.6);
  grd.addColorStop(0, sealed ? '#b8c0cc' : '#fff3c4');
  grd.addColorStop(1, sealed ? '#7c8594' : '#e8b24c');
  g.fillStyle = grd;
  g.fillText(title, w / 2, h * 0.4);
  g.font = `600 26px 'Noto Sans', sans-serif`;
  g.lineWidth = 6;
  g.strokeText(sub.toUpperCase(), w / 2, h * 0.8);
  g.fillStyle = sealed ? '#9aa3b0' : '#d9d0bd';
  g.fillText(sub.toUpperCase(), w / 2, h * 0.8);
}

/**
 * Portal visuals: exits and docks get a rotating rune circle on the ground, a
 * rising light curtain and drifting motes; dungeon / instance portals are
 * framed gates with a swirling vortex (inside the zone's `portal_frame` prop
 * when there is one). Every portal floats its label. Portals whose `requires`
 * flag is not met are shown sealed (dim, no curtain or vortex). The set is
 * re-synced every frame, so portals scripts add later appear too.
 */
export class PortalViews {
  readonly group = new THREE.Group();
  private items = new Map<string, PortalItem>();
  private time = { value: 0 };
  private emitAcc = 0;

  constructor(
    private heightAt: (x: number, z: number) => number,
    private waterLevel: number | null,
    private props: PropInstance[],
    private lib: AssetLibrary,
    private lights: PropLights | null,
    private particles: ParticleSystem | null,
    private zoneKind: string,
  ) {
    this.group.name = 'portals';
  }

  private ground(x: number, z: number): number {
    const h = this.heightAt(x, z);
    return this.waterLevel !== null ? Math.max(h, this.waterLevel) : h;
  }

  /** Creates/removes visuals to match `portals`; `isOpen` decides the sealed look. */
  sync(portals: readonly PortalDef[], isOpen: (p: PortalDef) => boolean): void {
    const seen = new Set<string>();
    for (const p of portals) {
      seen.add(p.id);
      let it = this.items.get(p.id);
      if (!it || it.def.x !== p.x || it.def.z !== p.z) {
        if (it) this.remove(it);
        it = this.create(p);
        this.items.set(p.id, it);
      }
      const open = isOpen(p);
      if (open !== it.open) this.setOpen(it, open);
    }
    for (const [id, it] of this.items) {
      if (seen.has(id)) continue;
      this.remove(it);
      this.items.delete(id);
    }
  }

  private shaderMat(frag: string, uniforms: Record<string, THREE.IUniform>, side: THREE.Side = THREE.FrontSide): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: frag,
      uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), ...uniforms },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side,
      fog: true,
    });
  }

  private create(p: PortalDef): PortalItem {
    const group = new THREE.Group();
    group.name = `portal_${p.id}`;
    const style: PortalStyle = p.kind === 'dungeon' || p.kind === 'instance' ? 'gate' : 'ring';
    const color = KIND_COLOR[p.kind].clone();
    const level = { value: 1 };
    const colorU = { value: color.clone() };
    const materials: THREE.Material[] = [];
    const active: THREE.Object3D[] = [];
    const y = this.ground(p.x, p.z);
    const base = new THREE.Vector3(p.x, y, p.z);
    group.position.copy(base);

    // A frame prop right at the portal (dungeon interiors) sets the gate's position and facing.
    const frameProp = this.props.find((q) => q.model === 'portal_frame' && Math.hypot(q.x - p.x, q.z - p.z) < 1.6);
    let facing = frameProp?.rot ?? 0;
    if (style === 'gate' && !frameProp) {
      // Face the camera (+z) so the vortex reads face-on in the quarter view.
      facing = 0;
    }

    // Rune circle on the ground.
    const runeR = p.radius + (style === 'gate' ? 1.0 : 0.7);
    const runeMat = this.shaderMat(RUNE_FRAG, { map: { value: runeTexture() }, uColor: colorU, uTime: this.time, uLevel: level, uSpin: { value: style === 'gate' ? -0.3 : 0.22 } });
    const rune = new THREE.Mesh(new THREE.PlaneGeometry(runeR * 2, runeR * 2), runeMat);
    rune.rotation.x = -Math.PI / 2;
    rune.position.y = 0.07;
    rune.renderOrder = 7;
    group.add(rune);
    materials.push(runeMat);

    const frame: THREE.Object3D | null = null;
    let labelY: number;
    if (style === 'ring') {
      const h = p.kind === 'dock' ? 2.4 : 3.4;
      const curtainMat = this.shaderMat(CURTAIN_FRAG, { uColor: colorU, uTime: this.time, uLevel: level }, THREE.DoubleSide);
      const geo = new THREE.CylinderGeometry(p.radius * 0.82, p.radius * 0.9, h, 40, 1, true);
      geo.translate(0, h / 2, 0);
      const curtain = new THREE.Mesh(geo, curtainMat);
      curtain.renderOrder = 12;
      group.add(curtain);
      materials.push(curtainMat);
      active.push(curtain);
      labelY = h + 1.1;
    } else {
      const gate = new THREE.Group();
      const fx = frameProp ? frameProp.x - p.x : 0;
      const fz = frameProp ? frameProp.z - p.z : 0;
      gate.position.set(fx, frameProp ? this.ground(frameProp.x, frameProp.z) - y : 0, fz);
      gate.rotation.y = facing;
      // Dark void behind the swirl gives the gate depth.
      const voidMat = new THREE.MeshBasicMaterial({ color: 0x07030d, transparent: true, opacity: 0.82, depthWrite: false, side: THREE.DoubleSide });
      const vortexMat = this.shaderMat(VORTEX_FRAG, { uColor: colorU, uTime: this.time, uLevel: level }, THREE.DoubleSide);
      const disc = new THREE.CircleGeometry(1, 48);
      const voidDisc = new THREE.Mesh(disc, voidMat);
      const vortex = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), vortexMat);
      for (const m of [voidDisc, vortex]) {
        m.scale.set(1.3, 1.85, 1);
        m.position.set(0, 2.2, 0);
      }
      voidDisc.position.z = -0.04;
      voidDisc.renderOrder = 6;
      vortex.renderOrder = 12;
      gate.add(voidDisc, vortex);
      materials.push(voidMat, vortexMat);
      active.push(voidDisc, vortex);
      group.add(gate);
      labelY = 6.1;
      // Field dungeon entrances have no frame of their own: stand one up.
      if (!frameProp && this.zoneKind === 'field') {
        void this.lib.load('portal_frame').then((t) => {
          if (t.placeholder || !this.items.has(p.id)) return;
          const f = this.lib.instantiate(t);
          f.traverse((o) => {
            const m = o as THREE.Mesh;
            if (m.isMesh) {
              m.castShadow = true;
              m.receiveShadow = true;
              // Geometry and materials belong to the shared template.
              m.userData.shared = true;
            }
          });
          gate.add(f);
          const it = this.items.get(p.id);
          if (it) it.frame = f;
        });
      }
    }

    // Label.
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const labelTex = new THREE.CanvasTexture(canvas);
    labelTex.colorSpace = THREE.SRGBColorSpace;
    const labelMat = new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthTest: false, depthWrite: false, fog: false });
    const label = new THREE.Sprite(labelMat);
    label.scale.set(4.2, 1.05, 1);
    label.position.set(0, labelY, 0);
    label.renderOrder = 20;
    group.add(label);
    materials.push(labelMat);

    this.group.add(group);
    const it: PortalItem = { def: p, group, style, color, materials, level, open: true, label, labelTex, base, facing, active, frame };
    this.drawLabel(it);
    // Redraw once web fonts are ready.
    void document.fonts?.load('600 46px Cinzel').then(() => this.drawLabel(it), () => {});
    this.lights?.setDynamic(`portal:${p.id}`, this.lightOf(it));
    return it;
  }

  private lightOf(it: PortalItem): LightSource {
    const c = it.open ? it.color : SEALED;
    return {
      kind: 'light',
      pos: new THREE.Vector3(it.base.x, it.base.y + (it.style === 'gate' ? 2.2 : 1.4), it.base.z),
      color: c.clone(),
      intensity: it.open ? 3.2 : 1.0,
      distance: it.def.radius + 4,
      ground: it.base.y,
    };
  }

  private drawLabel(it: PortalItem): void {
    drawLabel(it.labelTex.image, it.def.label, it.open ? KIND_LABEL[it.def.kind] : 'Sealed', !it.open);
    it.labelTex.needsUpdate = true;
  }

  private setOpen(it: PortalItem, open: boolean): void {
    it.open = open;
    for (const o of it.active) o.visible = open;
    this.drawLabel(it);
    this.lights?.setDynamic(`portal:${it.def.id}`, this.lightOf(it));
  }

  update(dt: number, focus: THREE.Vector3, density: number): void {
    this.time.value += dt;
    this.emitAcc += dt * 14 * density;
    const n = Math.floor(this.emitAcc);
    this.emitAcc -= n;
    for (const it of this.items.values()) {
      const d = Math.hypot(it.base.x - focus.x, it.base.z - focus.z);
      const target = it.open ? 1 : 0.35;
      it.level.value += (target - it.level.value) * Math.min(1, dt * 3);
      const col = it.open ? it.color : SEALED;
      for (const m of it.materials) {
        const u = (m as THREE.ShaderMaterial).uniforms as Record<string, THREE.IUniform> | undefined;
        const c = u?.uColor as THREE.IUniform<THREE.Color> | undefined;
        if (c) c.value.lerp(col, Math.min(1, dt * 4));
      }
      // Labels fade out far away, and right next to the portal (where they would cover the HUD).
      const lm = it.label.material;
      lm.opacity = THREE.MathUtils.clamp((52 - d) / 12, 0, 1) * THREE.MathUtils.clamp((d - 8) / 4, 0, 1);
      it.label.visible = lm.opacity > 0.01;
      // Rising motes around open portals near the player.
      if (!it.open || !this.particles || d > 45) continue;
      const ps = this.particles;
      for (let k = 0; k < n; k++) {
        const a = Math.random() * Math.PI * 2;
        const r = it.style === 'gate' ? 1.2 + Math.random() * 0.4 : it.def.radius * (0.75 + Math.random() * 0.2);
        let x = it.base.x + Math.cos(a) * r;
        let z = it.base.z + Math.sin(a) * r;
        let yy = it.base.y + 0.2;
        if (it.style === 'gate') {
          // Motes spill out of the vortex rim.
          const sx = Math.cos(it.facing);
          const sz = -Math.sin(it.facing);
          x = it.base.x + sx * Math.cos(a) * 1.3;
          z = it.base.z + sz * Math.cos(a) * 1.3;
          yy = it.base.y + 2.2 + Math.sin(a) * 1.8;
        }
        ps.spawn({
          x,
          y: yy,
          z,
          vx: (Math.random() - 0.5) * 0.3,
          vy: 0.6 + Math.random() * 0.9,
          vz: (Math.random() - 0.5) * 0.3,
          color: it.color.clone().multiplyScalar(2.6),
          colorEnd: it.color.clone().multiplyScalar(0.4),
          size: 0.1 + Math.random() * 0.08,
          sizeEnd: 0.02,
          life: 1.1 + Math.random() * 0.8,
          drag: 0.4,
        });
      }
    }
  }

  private remove(it: PortalItem): void {
    this.group.remove(it.group);
    it.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && !m.userData.shared) m.geometry.dispose();
    });
    for (const m of it.materials) m.dispose();
    it.labelTex.dispose();
    this.lights?.setDynamic(`portal:${it.def.id}`, null);
  }

  dispose(): void {
    for (const it of this.items.values()) this.remove(it);
    this.items.clear();
  }
}
